import { type RefObject } from "react";
import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";
import type { Editor } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import { Separator } from "@/components/ui/separator";
import { Toggle } from "@/components/ui/toggle";
import type { BlockInfo, StyleProperty } from "./documentEditor";
import { TextColorControl } from "./TextColorControl";
import { TextSizeInput } from "./TextSizeInput";

interface TextMarkControlsProps {
  /** The open session's TipTap instance, or null when none is open. */
  editor: Editor | null;
  /** The selected block the size and colour target when no session is open. */
  block: BlockInfo | null;
  /** The preview container, measured for what a block actually renders. */
  previewRef: RefObject<HTMLDivElement>;
  /** Whether the open session's root can hold block structure. */
  blocksEnabled: boolean;
  /** Blocks formatting even while a session is open (an upload is running). */
  disabled: boolean;
  /** Writes one inline-style property on the selected block. */
  onStyleChange: (property: StyleProperty, value: string) => void;
  /** Commits the session, for when focus leaves it from a typed-into knob. */
  onCommitSession: () => void;
}

/** What the toggles show with no session to read state from. */
const NO_ACTIVE_MARKS = {
  bold: false,
  italic: false,
  underline: false,
  bulletList: false,
  orderedList: false,
};

const IS_MAC =
  typeof navigator !== "undefined" && navigator.userAgent.includes("Mac");
const MOD_KEY = IS_MAC ? "⌘" : "Ctrl+";

/**
 * The toolbar's character-formatting group: the font size box and colour
 * palette, bold, italic, underline, and the bullet and numbered list toggles.
 * Every control is always mounted so the bar never reflows. The size and
 * colour pair and the mark toggles are context-aware - they style the
 * selected runs while a text session is open and the selected block otherwise
 * (landing as the mapped container attribute on an `<fp-column>`, e.g.
 * `decoration="underline"`) - so they only disable when neither target can
 * take type styling. Emphasis a block carries as a whole (its `weight` /
 * `font-style` / `decoration` prop or the matching inline style) reads as
 * pressed even inside a session, and unpressing it clears the block prop -
 * a run mark cannot negate block-level emphasis, so the block channel is the
 * only unset that works. The list toggles disable without a session, and stay
 * disabled in sessions whose schema has no lists (a `<span>` root). Controls preventDefault on mousedown
 * so the session keeps focus - a blur would commit it - except the two that
 * have to be typed into, which hold the session open instead
 * (textSessionFocus.ts).
 * @param editor - The open session's TipTap instance, or null
 * @param block - The selected block styled when no session is open
 * @param previewRef - The preview container, measured for rendered styles
 * @param blocksEnabled - Whether the session root can hold block structure
 * @param disabled - Blocks formatting even while a session is open
 * @param onStyleChange - Writes one inline-style property on the block
 * @param onCommitSession - Commits the session
 * @returns The character-formatting controls
 */
export function TextMarkControls({
  editor,
  block,
  previewRef,
  blocksEnabled,
  disabled,
  onStyleChange,
  onCommitSession,
}: TextMarkControlsProps) {
  // Subscribing to the transaction counter (rather than selecting the marks
  // out of the snapshot) keeps the toggles correct the moment a session opens:
  // the snapshot still carries the previous editor until the next transaction.
  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber,
  });
  const active = editor ? activeMarks(editor) : NO_ACTIVE_MARKS;
  const marksDisabled = disabled || editor === null;
  const blocksDisabled = marksDisabled || !blocksEnabled;
  const styleDisabled = disabled || (editor === null && blockUnstylable(block));

  return (
    <div
      className="flex shrink-0 items-center gap-0.5"
      onMouseDown={(event) => event.preventDefault()}
    >
      <TextSizeInput
        editor={editor}
        block={block}
        previewRef={previewRef}
        disabled={styleDisabled}
        onStyleChange={onStyleChange}
        onCommitSession={onCommitSession}
      />
      <TextColorControl
        editor={editor}
        block={block}
        previewRef={previewRef}
        disabled={styleDisabled}
        onStyleChange={onStyleChange}
        onCommitSession={onCommitSession}
      />
      <Separator orientation="vertical" className="mx-0.5 h-5" />
      <Toggle
        size="sm"
        pressed={(editor !== null && active.bold) || blockBold(block)}
        disabled={editor ? marksDisabled : styleDisabled}
        onPressedChange={() => {
          if (editor && active.bold) {
            editor.chain().focus().toggleBold().run();
            return;
          }
          if (blockBold(block)) {
            onStyleChange("fontWeight", "");
            return;
          }
          if (editor) {
            editor.chain().focus().toggleBold().run();
            return;
          }
          onStyleChange("fontWeight", "700");
        }}
        title={`Bold (${MOD_KEY}B)`}
        aria-label="Bold"
      >
        <Bold />
      </Toggle>
      <Toggle
        size="sm"
        pressed={(editor !== null && active.italic) || blockItalic(block)}
        disabled={editor ? marksDisabled : styleDisabled}
        onPressedChange={() => {
          if (editor && active.italic) {
            editor.chain().focus().toggleItalic().run();
            return;
          }
          if (blockItalic(block)) {
            onStyleChange("fontStyle", "");
            return;
          }
          if (editor) {
            editor.chain().focus().toggleItalic().run();
            return;
          }
          onStyleChange("fontStyle", "italic");
        }}
        title={`Italic (${MOD_KEY}I)`}
        aria-label="Italic"
      >
        <Italic />
      </Toggle>
      <Toggle
        size="sm"
        pressed={
          (editor !== null && active.underline) || blockUnderlined(block)
        }
        disabled={editor ? marksDisabled : styleDisabled}
        onPressedChange={() => {
          if (editor && active.underline) {
            editor.chain().focus().toggleUnderline().run();
            return;
          }
          if (blockUnderlined(block)) {
            onStyleChange("textDecoration", "");
            return;
          }
          if (editor) {
            editor.chain().focus().toggleUnderline().run();
            return;
          }
          onStyleChange("textDecoration", "underline");
        }}
        title={`Underline (${MOD_KEY}U)`}
        aria-label="Underline"
      >
        <Underline />
      </Toggle>
      <Separator orientation="vertical" className="mx-0.5 h-5" />
      <Toggle
        size="sm"
        pressed={active.bulletList}
        disabled={blocksDisabled}
        onPressedChange={() => editor?.chain().focus().toggleBulletList().run()}
        title="Bullet list"
        aria-label="Bullet list"
      >
        <List />
      </Toggle>
      <Toggle
        size="sm"
        pressed={active.orderedList}
        disabled={blocksDisabled}
        onPressedChange={() =>
          editor?.chain().focus().toggleOrderedList().run()
        }
        title="Numbered list"
        aria-label="Numbered list"
      >
        <ListOrdered />
      </Toggle>
    </div>
  );
}

/**
 * Whether the selected block's authored weight makes it bold.
 * @param block - The selected block, or null
 * @returns True when the block declares a bold weight
 */
function blockBold(block: BlockInfo | null): boolean {
  const weight = block?.styles.fontWeight ?? "";
  return weight === "bold" || Number.parseInt(weight, 10) >= 600;
}

/**
 * Whether the selected block's authored font style italicizes it.
 * @param block - The selected block, or null
 * @returns True when the block declares an italic style
 */
function blockItalic(block: BlockInfo | null): boolean {
  return block?.styles.fontStyle.includes("italic") ?? false;
}

/**
 * Whether the selected block's authored decoration underlines it.
 * @param block - The selected block, or null
 * @returns True when the block declares an underline
 */
function blockUnderlined(block: BlockInfo | null): boolean {
  return block?.styles.textDecoration.includes("underline") ?? false;
}

/**
 * Whether a selected block can take no type styling: nothing selected, a
 * repeater whose stamped output never renders the source tag's authored
 * style, a picture, or a section whose styling would silently restyle every
 * block inside it.
 * @param block - The selected block, or null
 * @returns True when the size and colour pair has no writable target
 */
function blockUnstylable(block: BlockInfo | null): boolean {
  return (
    block === null ||
    block.repeater !== null ||
    block.tagName === "fp-image" ||
    block.tagName === "section" ||
    block.tagName === "div"
  );
}

/**
 * Reads which marks and list nodes are active at the session's selection.
 * @param editor - The open session's TipTap instance
 * @returns The pressed state of each toggle
 */
function activeMarks(editor: Editor): typeof NO_ACTIVE_MARKS {
  return {
    bold: editor.isActive("bold"),
    italic: editor.isActive("italic"),
    underline: editor.isActive("underline"),
    bulletList: editor.isActive("bulletList"),
    orderedList: editor.isActive("orderedList"),
  };
}
